import React, { useState, useRef, useEffect } from 'react';
import Icon from '../../../components/AppIcon';
import Button from '../../../components/ui/Button';

const FacilityMap = ({ assets = [], vehicles = [], alerts = [], onAssetClick, onVehicleClick, onAlertClick }) => {
  const [selectedLayer, setSelectedLayer] = useState('all');
  const [zoomLevel, setZoomLevel] = useState(1);
  const [panPosition, setPanPosition] = useState({ x: 0, y: 0 });
  const [isDragging, setIsDragging] = useState(false);
  const [dragStart, setDragStart] = useState({ x: 0, y: 0 });
  const mapRef = useRef(null);

  const layerOptions = [
    { value: 'all', label: 'All', icon: 'Layers' },
    { value: 'assets', label: 'Assets', icon: 'Package' },
    { value: 'vehicles', label: 'Vehicles', icon: 'Truck' },
    { value: 'alerts', label: 'Alerts', icon: 'AlertTriangle' }
  ];

  const getAssetIcon = (type) => {
    switch (type) {
      case 'forklift': return 'Forklift';
      case 'conveyor': return 'ArrowRight';
      case 'robot': return 'Bot';
      case 'scanner': return 'Scan';
      case 'dock': return 'Truck';
      default: return 'Package';
    }
  };

  const getAssetColor = (status) => {
    switch (status) {
      case 'active': return 'text-success';
      case 'idle': return 'text-warning';
      case 'maintenance': return 'text-error';
      case 'offline': return 'text-muted-foreground';
      default: return 'text-primary';
    }
  };

  const getVehicleIcon = (type) => {
    switch (type) {
      case 'truck': return 'Truck';
      case 'van': return 'Car';
      case 'forklift': return 'Forklift';
      default: return 'Navigation';
    }
  };

  const getVehicleColor = (status) => {
    switch (status) {
      case 'moving': return 'text-success';
      case 'idle': return 'text-warning';
      case 'maintenance': return 'text-error';
      case 'offline': return 'text-muted-foreground';
      default: return 'text-primary';
    }
  };

  const getAlertColor = (severity) => {
    switch (severity) {
      case 'critical': return 'text-error';
      case 'warning': return 'text-warning';
      case 'info': return 'text-primary';
      default: return 'text-muted-foreground';
    }
  };

  const handleMouseDown = (e) => {
    setIsDragging(true);
    setDragStart({
      x: e?.clientX - panPosition?.x,
      y: e?.clientY - panPosition?.y
    });
  };

  const handleMouseMove = (e) => {
    if (!isDragging) return;
    
    setPanPosition({
      x: e?.clientX - dragStart?.x,
      y: e?.clientY - dragStart?.y
    });
  };

  const handleMouseUp = () => {
    setIsDragging(false);
  };

  const handleZoomIn = () => {
    setZoomLevel(prev => Math.min(prev + 0.2, 3));
  };

  const handleZoomOut = () => {
    setZoomLevel(prev => Math.max(prev - 0.2, 0.5));
  };

  const handleResetView = () => {
    setZoomLevel(1);
    setPanPosition({ x: 0, y: 0 });
  };

  const shouldShowItem = (type) => {
    return selectedLayer === 'all' || selectedLayer === type;
  };

  useEffect(() => {
    const handleGlobalMouseMove = (e) => handleMouseMove(e);
    const handleGlobalMouseUp = () => handleMouseUp();

    if (isDragging) {
      document.addEventListener('mousemove', handleGlobalMouseMove);
      document.addEventListener('mouseup', handleGlobalMouseUp);
    }

    return () => {
      document.removeEventListener('mousemove', handleGlobalMouseMove);
      document.removeEventListener('mouseup', handleGlobalMouseUp);
    };
  }, [isDragging, dragStart]);

  return (
    <div className="bg-card border border-border rounded-lg h-full flex flex-col">
      <div className="p-4 border-b border-border">
        <div className="flex items-center justify-between mb-4">
          <h3 className="text-lg font-semibold text-foreground">Facility Overview</h3>
          <div className="flex items-center space-x-2">
            <div className="status-indicator status-online"></div>
            <span className="text-xs text-muted-foreground">Real-time</span>
          </div>
        </div>

        <div className="flex items-center justify-between">
          <div className="flex items-center space-x-1">
            {layerOptions?.map((layer) => (
              <button
                key={layer?.value}
                onClick={() => setSelectedLayer(layer?.value)}
                className={`flex items-center space-x-1 px-3 py-1.5 text-xs rounded-md nav-transition ${
                  selectedLayer === layer?.value
                    ? 'bg-primary text-primary-foreground'
                    : 'bg-muted text-muted-foreground hover:bg-border'
                }`}
              >
                <Icon name={layer?.icon} size={14} />
                <span>{layer?.label}</span>
              </button>
            ))}
          </div>

          <div className="flex items-center space-x-1">
            <Button variant="ghost" size="xs" onClick={handleZoomOut}>
              <Icon name="ZoomOut" size={16} />
            </Button>
            <span className="text-xs text-muted-foreground px-2">
              {Math.round(zoomLevel * 100)}%
            </span>
            <Button variant="ghost" size="xs" onClick={handleZoomIn}>
              <Icon name="ZoomIn" size={16} />
            </Button>
            <Button variant="ghost" size="xs" onClick={handleResetView}>
              <Icon name="RotateCcw" size={16} />
            </Button>
          </div>
        </div>
      </div>
      <div className="flex-1 relative overflow-hidden bg-muted/20">
        <div
          ref={mapRef}
          className="w-full h-full relative cursor-move"
          onMouseDown={handleMouseDown}
          style={{
            transform: `translate(${panPosition?.x}px, ${panPosition?.y}px) scale(${zoomLevel})`,
            transformOrigin: 'center center'
          }}
        >
          {/* Facility Layout Background */}
          <div className="absolute inset-0 bg-gradient-to-br from-muted/10 to-muted/30">
            {/* Grid Pattern */}
            <div 
              className="absolute inset-0 opacity-20"
              style={{
                backgroundImage: `
                  linear-gradient(rgba(0,0,0,0.1) 1px, transparent 1px),
                  linear-gradient(90deg, rgba(0,0,0,0.1) 1px, transparent 1px)
                `,
                backgroundSize: '50px 50px'
              }}
            />

            {/* Facility Zones */}
            <div className="absolute top-10 left-10 w-48 h-32 bg-primary/10 border-2 border-primary/30 rounded-lg flex items-center justify-center">
              <span className="text-sm font-medium text-primary">Warehouse A</span>
            </div>
            <div className="absolute top-10 right-10 w-48 h-32 bg-accent/10 border-2 border-accent/30 rounded-lg flex items-center justify-center">
              <span className="text-sm font-medium text-accent">Warehouse B</span>
            </div>
            <div className="absolute bottom-10 left-10 w-48 h-24 bg-success/10 border-2 border-success/30 rounded-lg flex items-center justify-center">
              <span className="text-sm font-medium text-success">Loading Dock</span>
            </div>
            <div className="absolute bottom-10 right-10 w-48 h-24 bg-warning/10 border-2 border-warning/30 rounded-lg flex items-center justify-center">
              <span className="text-sm font-medium text-warning">Maintenance</span>
            </div>
          </div>

          {/* Assets */}
          {shouldShowItem('assets') && assets?.map((asset) => (
            <div
              key={asset?.id}
              className="absolute transform -translate-x-1/2 -translate-y-1/2 cursor-pointer hover:scale-110 nav-transition"
              style={{ left: `${asset?.x}%`, top: `${asset?.y}%` }}
              onClick={() => onAssetClick && onAssetClick(asset)}
            >
              <div className={`p-2 rounded-lg bg-card border-2 shadow-sm ${
                asset?.status === 'active' ? 'border-success' : 
                asset?.status === 'idle' ? 'border-warning' : 
                asset?.status === 'maintenance' ? 'border-error' : 'border-border'
              }`}>
                <Icon 
                  name={getAssetIcon(asset?.type)} 
                  size={20} 
                  className={getAssetColor(asset?.status)} 
                />
              </div>
              <div className="absolute -bottom-6 left-1/2 transform -translate-x-1/2 bg-card px-2 py-1 rounded text-xs font-medium text-foreground shadow-sm whitespace-nowrap">
                {asset?.name}
              </div>
            </div>
          ))}

          {/* Vehicles */}
          {shouldShowItem('vehicles') && vehicles?.map((vehicle) => (
            <div
              key={vehicle?.id}
              className="absolute transform -translate-x-1/2 -translate-y-1/2 cursor-pointer hover:scale-110 nav-transition"
              style={{ left: `${vehicle?.x}%`, top: `${vehicle?.y}%` }}
              onClick={() => onVehicleClick && onVehicleClick(vehicle)}
            >
              <div className={`p-2 rounded-full bg-card border-2 shadow-sm ${
                vehicle?.status === 'moving' ? 'border-success' : 
                vehicle?.status === 'idle' ? 'border-warning' : 
                vehicle?.status === 'maintenance' ? 'border-error' : 'border-border'
              }`}>
                <Icon 
                  name={getVehicleIcon(vehicle?.type)} 
                  size={18} 
                  className={getVehicleColor(vehicle?.status)} 
                />
              </div>
              <div className="absolute -bottom-6 left-1/2 transform -translate-x-1/2 bg-card px-2 py-1 rounded text-xs font-medium text-foreground shadow-sm whitespace-nowrap">
                {vehicle?.id}
              </div>
            </div>
          ))}

          {/* Alert Hotspots */}
          {shouldShowItem('alerts') && alerts?.map((alert) => (
            <div
              key={alert?.id}
              className="absolute transform -translate-x-1/2 -translate-y-1/2 cursor-pointer"
              style={{ left: `${alert?.x}%`, top: `${alert?.y}%` }}
              onClick={() => onAlertClick && onAlertClick(alert)}
            >
              <div className={`p-2 rounded-full animate-pulse ${
                alert?.severity === 'critical' ? 'bg-error' : 
                alert?.severity === 'warning' ? 'bg-warning' : 'bg-primary'
              }`}>
                <Icon name="AlertTriangle" size={16} color="white" />
              </div>
              <div className="absolute -bottom-6 left-1/2 transform -translate-x-1/2 bg-card px-2 py-1 rounded text-xs font-medium text-foreground shadow-sm whitespace-nowrap">
                {alert?.title}
              </div>
            </div>
          ))}
        </div>

        {/* Legend */}
        <div className="absolute bottom-4 left-4 bg-card border border-border rounded-lg p-3 shadow-sm">
          <h4 className="text-xs font-medium text-foreground mb-2">Legend</h4>
          <div className="space-y-1">
            <div className="flex items-center space-x-2">
              <div className="w-3 h-3 rounded border-2 border-success"></div>
              <span className="text-xs text-muted-foreground">Active/Moving</span>
            </div>
            <div className="flex items-center space-x-2">
              <div className="w-3 h-3 rounded border-2 border-warning"></div>
              <span className="text-xs text-muted-foreground">Idle/Warning</span>
            </div>
            <div className="flex items-center space-x-2">
              <div className="w-3 h-3 rounded border-2 border-error"></div>
              <span className="text-xs text-muted-foreground">Maintenance/Critical</span>
            </div>
          </div>
        </div>

        {/* Stats Overlay */}
        <div className="absolute top-4 right-4 bg-card border border-border rounded-lg p-3 shadow-sm">
          <div className="grid grid-cols-2 gap-3 text-center">
            <div>
              <div className="text-lg font-bold text-success">{assets?.filter(a => a?.status === 'active')?.length}</div>
              <div className="text-xs text-muted-foreground">Active Assets</div>
            </div>
            <div>
              <div className="text-lg font-bold text-primary">{vehicles?.filter(v => v?.status === 'moving')?.length}</div>
              <div className="text-xs text-muted-foreground">Moving Vehicles</div>
            </div>
            <div>
              <div className="text-lg font-bold text-warning">{alerts?.filter(a => a?.severity === 'warning')?.length}</div>
              <div className="text-xs text-muted-foreground">Warnings</div>
            </div>
            <div>
              <div className="text-lg font-bold text-error">{alerts?.filter(a => a?.severity === 'critical')?.length}</div>
              <div className="text-xs text-muted-foreground">Critical</div>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default FacilityMap;